import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { checkCaseFiles } from "../../store/superAdmin/fileCheckerSlice";

type FileCheckStatusProps = {
  caseId?: string;
};

const FileCheckStatus = ({ caseId }: FileCheckStatusProps) => {
  const dispatch = useAppDispatch();
  const { loading, error, result } = useAppSelector((s) => s.fileChecker);
  const { caseFiles } = useAppSelector((s) => s.fileUpload);

  const fileNamesKey = useMemo(
    () =>
      caseFiles
        .filter((f) => !caseId || f.case_id === caseId)
        .map((f) => f.file_name)
        .sort()
        .join("|"),
    [caseFiles, caseId]
  );

  useEffect(() => {
    if (!caseId) return;
    dispatch(checkCaseFiles({ case_id: caseId }));
  }, [caseId, fileNamesKey, dispatch]);

  if (!caseId) return null;

  if (loading && (!result || result.case_id !== caseId)) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
        Checking files...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        File check failed: {error}
      </div>
    );
  }

  if (!result || result.case_id !== caseId) return null;

  if (result.success) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
        File check: all required files present
      </div>
    );
  }

  const errors = result.errors || [];

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      <p className="mb-1 font-medium">
        File check: {errors.length} missing file issue
        {errors.length === 1 ? "" : "s"}
      </p>
      <div>
        {errors.map((err, index) => (
          <div key={`${err}-${index}`}>• {err}</div>
        ))}
      </div>
    </div>
  );
};

export default FileCheckStatus;
